import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import {
  PapiDaSharedOriginalsOrder,
  PapiOriginalsResponse,
  PapiDeviation,
} from '@wix/da-papi-types';
import {
  BiData,
  BiContextProvidedValue,
  OriginalsOrderAcceptClickBiEvent,
  OriginalsOrderDetailsModalDeclineClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';

import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import OrderSummary from '@wix/da-ds/pkg/originals/OrderSummary';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { usePushModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushModal';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { DuperbrowseDisabled } from '@wix/da-shared-react/pkg/Duperbrowse';
import QuestionnaireAnswers from './_partials/QuestionnaireAnswers';
import {
  declineOriginalOrder,
  acceptOriginalOrder,
  fetchOriginalsOrderDetails,
} from '../../actions/studio/originals';
import s from './OriginalsOrderDetailsModal.scss';

interface Props {
  deviationId: number;
  responseId: number;
  /** When present (e.g. originals-by-deviation list), scopes stream refresh to this grouping deviation. */
  parentDeviation?: PapiDeviation;
  onRequestClose: () => void;
}

const OriginalsOrderDetailsModal: React.FC<Props> = ({
  deviationId,
  responseId,
  parentDeviation,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const [deviation, setDeviation] = useState<PapiDeviation | null>(null);
  const [order, setOrder] = useState<PapiOriginalsResponse | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(true);
  const logBiEvent = useBiLogger();

  const pushOriginalsDeclineModal = usePushModal(ModalType.STUDIO_ORIGINALS_DECLINE);
  const pushOriginalsDeclineSuccessModal = usePushModal(ModalType.STUDIO_ORIGINALS_DECLINE_SUCCESS);
  const pushOriginalsAcceptSuccessModal = usePushModal(ModalType.STUDIO_ORIGINALS_ACCEPT_SUCCESS);

  const summaryProps = useMemo(() => {
    if (!order || !deviation) {
      return undefined;
    }

    const { basePrice = 0 } = deviation.extended?.originals ?? {};
    const orderAnswers = order.orderAnswers ?? [];
    const selectedAddons = orderAnswers.reduce((acc, { question, answerIds }) => {
      const selectedAnswers =
        question?.answers?.filter(a => answerIds?.includes(a.answerId ?? 0)) ?? [];
      if (selectedAnswers.length > 0) {
        return acc.concat(selectedAnswers);
      }
      return acc;
    }, []);

    return { basePrice, selectedAddons };
  }, [order, deviation]);

  const onDecline = useCallback(
    (reason: string, onSuccess: () => void, onError: () => void) => {
      dispatch(
        declineOriginalOrder({
          deviationId,
          responseId,
          reason,
          parentDeviation,
          onSuccess: () => {
            onSuccess();
            pushOriginalsDeclineSuccessModal({
              requestingUser: order?.user,
            });
            onRequestClose();
          },
          onError: () => {
            onError();
            dispatch(addErrorNotification(t('common.somethingWentWrong')));
            onRequestClose();
          },
        })
      );
    },
    [
      dispatch,
      deviationId,
      responseId,
      order?.user,
      onRequestClose,
      t,
      pushOriginalsDeclineSuccessModal,
      parentDeviation,
    ]
  );

  const onDeclineClick = useCallback(() => {
    pushOriginalsDeclineModal({
      original: deviation,
      order,
      onDecline,
    });
  }, [pushOriginalsDeclineModal, deviation, order, onDecline]);

  const onAcceptClickBiLog = useCallback(() => {
    logBiEvent(
      BiData<OriginalsOrderAcceptClickBiEvent>({
        evid: 773,
        responseid: responseId,
        recipientid: order?.user?.useridUuid,
        typeid: getDeviationResourceType(deviation),
        itemid: deviation?.deviationId,
        view: BiContextProvidedValue,
        component: BiContextProvidedValue,
      })
    );
  }, [logBiEvent, deviation, order, responseId]);

  const onAcceptClick = useCallback(() => {
    setIsBusy(true);
    onAcceptClickBiLog();
    dispatch(
      acceptOriginalOrder({
        deviationId,
        responseId,
        parentDeviation,
        onSuccess: () => {
          pushOriginalsAcceptSuccessModal({
            requestingUser: order?.user,
          });
          onRequestClose();
        },
        onError: () => {
          dispatch(addErrorNotification(t('common.somethingWentWrong')));
          onRequestClose();
        },
      })
    );
  }, [
    dispatch,
    deviationId,
    responseId,
    parentDeviation,
    order?.user,
    pushOriginalsAcceptSuccessModal,
    onRequestClose,
    onAcceptClickBiLog,
    t,
  ]);

  const showActionButtons = useMemo(() => order?.status === 'pending', [order]);

  useEffect(() => {
    dispatch(
      fetchOriginalsOrderDetails({
        deviationId,
        responseId,
        onSuccess: (data: PapiDaSharedOriginalsOrder) => {
          setIsBusy(false);
          setOrder(data.response);
          setDeviation(data.deviation);
        },
        onError: () => {
          dispatch(addErrorNotification(t('common.somethingWentWrong')));
          onRequestClose();
        },
      })
    );
  }, [deviationId, dispatch, onRequestClose, responseId, t]);

  return (
    <Modal
      className={s['root']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      headerPadding={isMobile ? 'slim' : 'default'}
      footerPadding={isMobile ? 'horizontally-less' : 'default'}
      heading={t('studio.modals.orderDetails.title')}
      footer={
        showActionButtons && (
          <div className={s['footer']}>
            <TextButton
              key="decline"
              className={cn(s['button'], s['button-decline'])}
              onClick={onDeclineClick}
              size="medium"
              variant="main"
              disabled={isBusy}
              biData={BiData<OriginalsOrderDetailsModalDeclineClickBiEvent>({
                evid: 774,
                responseid: order?.responseId,
                recipientid: order?.user?.useridUuid,
                itemid: deviationId,
                typeid: getDeviationResourceType(deviation),
                view: BiContextProvidedValue,
                component: BiContextProvidedValue,
              })}
            >
              {t('studio.modals.orderDetails.actions.decline')}
            </TextButton>
            <BrandedButton
              key="accept"
              className={s['button']}
              onClick={onAcceptClick}
              size="medium"
              variant="brand-green"
              disabled={isBusy}
            >
              {t('studio.modals.orderDetails.actions.accept')}
            </BrandedButton>
          </div>
        )
      }
      onRequestClose={onRequestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick={false}
      isOpen
      isFullscreen={isMobile}
      withScrollableContentWrapper
    >
      <DuperbrowseDisabled>
        {order && (
          <>
            <QuestionnaireAnswers order={order} />
            <OrderSummary {...summaryProps} className={isMobile ? s['order-summary'] : undefined} />
          </>
        )}
        {order === null && <LoadingIndicator view="llama" />}
      </DuperbrowseDisabled>
    </Modal>
  );
};

export default OriginalsOrderDetailsModal;
